import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { User, Lock, Save, Bell, Shield, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { LEGAL_LINKS } from '@/config/links';

export const UserProfile = () => {
  const { user, checkAuth } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Password strength analysis (0-4)
  const passwordStrength = (() => {
    const pw = newPassword;
    if (!pw) return { score: 0, label: '', color: '', width: '0%' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
    score += Math.max(0, classes - 1);
    score = Math.min(4, score);
    const map = [
      { label: 'Çok Zayıf', color: 'bg-red-500', text: 'text-red-600', width: '20%' },
      { label: 'Zayıf', color: 'bg-orange-500', text: 'text-orange-600', width: '40%' },
      { label: 'Orta', color: 'bg-yellow-500', text: 'text-yellow-700', width: '60%' },
      { label: 'Güçlü', color: 'bg-lime-500', text: 'text-lime-700', width: '80%' },
      { label: 'Çok Güçlü', color: 'bg-green-600', text: 'text-green-700', width: '100%' },
    ];
    return { score, ...map[score] };
  })();

  const passwordMeetsPolicy = (() => {
    const pw = newPassword;
    if (pw.length < 8) return false;
    const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
    return classes >= 3;
  })();

  // Notification preferences (defaults to true for legacy users)
  const [notifySms, setNotifySms] = useState(user?.notify_sms ?? true);
  const [notifyEmail, setNotifyEmail] = useState(user?.notify_email ?? true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    setNotifySms(user?.notify_sms ?? true);
    setNotifyEmail(user?.notify_email ?? true);
    setName(user?.name || '');
  }, [user]);

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Ad soyad boş olamaz');
      return;
    }
    setSavingName(true);
    try {
      await axios.put(`${API_URL}/api/profile`, { name: name.trim() }, { withCredentials: true });
      await checkAuth();
      toast.success('İsim güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSavingName(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error('Mevcut şifrenizi girin');
      return;
    }
    if (!passwordMeetsPolicy) {
      toast.error('Şifre en az 8 karakter olmalı; büyük harf, küçük harf, rakam ve özel karakterden en az 3 türü içermelidir');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('Yeni şifre mevcut şifreyle aynı olamaz');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(
        `${API_URL}/api/profile`,
        { current_password: currentPassword, password: newPassword },
        { withCredentials: true },
      );
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Şifre güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre güncelleme başarısız');
    } finally {
      setSavingPassword(false);
    }
  };

  const updatePreference = async (field, value) => {
    setSavingPrefs(true);
    // Optimistic update
    if (field === 'notify_sms') setNotifySms(value);
    if (field === 'notify_email') setNotifyEmail(value);
    try {
      await axios.put(
        `${API_URL}/api/profile`,
        { [field]: value },
        { withCredentials: true }
      );
      await checkAuth();
      toast.success('Tercih güncellendi');
    } catch (error) {
      // Revert on error
      if (field === 'notify_sms') setNotifySms(!value);
      if (field === 'notify_email') setNotifyEmail(!value);
      toast.error(error.response?.data?.detail || 'Tercih güncellenemedi');
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="profile-page-title">
            Profil Ayarları
          </h1>
          <p className="text-base text-[#595959] mt-2">Kişisel bilgilerinizi güncelleyin</p>
        </div>

        <div className="space-y-6">
          {/* Personal Info */}
          <Card className="border border-[#E5E5E5]" data-testid="profile-info-card">
            <CardHeader>
              <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                <User className="w-5 h-5" strokeWidth={1.5} />
                Kişisel Bilgiler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleNameSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm text-[#595959]">E-posta</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="bg-[#F9F8F6] text-[#595959]"
                    data-testid="profile-email-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-[#595959]">Ad Soyad</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ad Soyad"
                    data-testid="profile-name-input"
                  />
                </div>
                {user?.apartment_number && (
                  <div>
                    <Label className="text-sm text-[#595959]">{user?.labels?.identifier || 'Numara'}</Label>
                    <Input
                      value={user.apartment_number}
                      disabled
                      className="bg-[#F9F8F6] text-[#595959]"
                      data-testid="profile-apartment-input"
                    />
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={savingName || name === user?.name}
                  className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white"
                  data-testid="profile-save-name-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingName ? 'Kaydediliyor...' : 'İsmi Kaydet'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card className="border border-[#E5E5E5]" data-testid="profile-notifications-card">
            <CardHeader>
              <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                <Bell className="w-5 h-5" strokeWidth={1.5} />
                Bildirim Tercihleri
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium text-[#1A1A2E] text-sm">SMS Bildirimleri</p>
                  <p className="text-xs text-[#595959] mt-0.5">
                    Ödeme hatırlatma, randevu ve önemli güncellemeler için SMS al.
                  </p>
                </div>
                <Switch
                  checked={notifySms}
                  onCheckedChange={(v) => updatePreference('notify_sms', v)}
                  disabled={savingPrefs}
                  data-testid="profile-notify-sms-switch"
                />
              </div>

              <div className="h-px bg-[#E5E5E5]" />

              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium text-[#1A1A2E] text-sm">E-posta Bildirimleri</p>
                  <p className="text-xs text-[#595959] mt-0.5">
                    Ödeme makbuzu, sözleşme ve bilgilendirme e-postalarını al.
                  </p>
                </div>
                <Switch
                  checked={notifyEmail}
                  onCheckedChange={(v) => updatePreference('notify_email', v)}
                  disabled={savingPrefs}
                  data-testid="profile-notify-email-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Password */}
          <Card className="border border-[#E5E5E5]" data-testid="profile-password-card">
            <CardHeader>
              <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                <Lock className="w-5 h-5" strokeWidth={1.5} />
                Şifre Değiştir
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm text-[#475569]">Mevcut Şifre</Label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Şu anki şifreniz"
                    autoComplete="current-password"
                    data-testid="profile-current-password-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-[#475569]">Yeni Şifre</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="En az 8 karakter, büyük/küçük harf + rakam"
                    autoComplete="new-password"
                    data-testid="profile-new-password-input"
                  />
                  {/* Strength meter */}
                  {newPassword && (
                    <div className="mt-2" data-testid="password-strength-meter">
                      <div className="h-1.5 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{ width: passwordStrength.width }}
                        />
                      </div>
                      <p className={`text-xs mt-1 font-medium ${passwordStrength.text}`} data-testid="password-strength-label">
                        Güç: {passwordStrength.label}
                      </p>
                      {!passwordMeetsPolicy && (
                        <p className="text-[11px] text-[#94A3B8] mt-1">
                          Min. 8 karakter · büyük harf, küçük harf, rakam, özel karakterden en az 3'ü
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-[#475569]">Yeni Şifre (Tekrar)</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Şifrenizi tekrar girin"
                    autoComplete="new-password"
                    data-testid="profile-confirm-password-input"
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-600 mt-1" data-testid="password-mismatch">Şifreler eşleşmiyor</p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword || !passwordMeetsPolicy || newPassword !== confirmPassword}
                  className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white"
                  data-testid="profile-save-password-button"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {savingPassword ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Legal links */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-[#999] pt-2 pb-6" data-testid="profile-legal-links">
            <a
              href={LEGAL_LINKS.privacy}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-[#2563EB] hover:underline"
              data-testid="profile-privacy-link"
            >
              <Shield className="w-3.5 h-3.5" />
              Gizlilik Politikası
            </a>
            <span className="text-[#E5E5E5]">|</span>
            <a
              href={LEGAL_LINKS.kvkk}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-[#2563EB] hover:underline"
              data-testid="profile-kvkk-link"
            >
              <FileText className="w-3.5 h-3.5" />
              KVKK Aydınlatma Metni
            </a>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
